import React, { useEffect, useState, useMemo, useRef } from "react";
import { inject, observer } from "mobx-react";

import { makeStyles, SvgIcon } from "@material-ui/core";

import styled from "styled-components";
import ScrollToBottom from "react-scroll-to-bottom";

import Zoom from "@material-ui/core/Zoom";

import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";

import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import { MdSend } from "react-icons/md";
import { FaArrowRight } from "react-icons/fa";

import ApiClient from "utils/ApiClient";

import PacksTaskAction from "./tasks/PacksTaskAction";
import SheetsTaskAction from "./tasks/SheetsTaskAction";
import SubsheetsTaskAction from "./tasks/SubsheetsTaskAction";
import AddGeneralFieldsTaskAction from "./tasks/AddGeneralFieldsTaskAction";
import LookupsTaskAction from "./tasks/LookupsTaskAction";
import FormulaeTaskAction from "./tasks/FormulaeTaskAction";
import CreateDummyDataTaskAction from "./tasks/CreateDummyDataTaskAction";

import useChatCompletion from "./hooks/useChatCompletion";
import useChatSettings from "./hooks/useChatSettings";
import useChatModel from "./hooks/useChatModel";

import UserChat from "./components/UserChat";
import AssistantChat from "./components/AssistantChat";
import StyledLinearProgress from "./components/StyledLinearProgress";

import StyledIconButton from "./buttons/StyledIconButton";

import assistantConfig from "./configs/assistant.config";

import {
  CHAT_MESSAGE_TAGS,
  FIELD_TYPES,
  OPENAI_MODELS,
  OPENAI_MODEL_VARIANTS,
  CHAT_CONVERSATON_ORG_USED_HEADER,
  TASK_NAMES,
} from "./lib/constants";

import {
  cx,
  fitResponseToFunctions,
  generateUUID,
  getValueFromLocalStorage,
} from "./lib/utils";

import { getLogoSrc, getTitle } from "app/common/utils/Logo";

import gpt3Encoder from "./modules/gpt-3-encoder";
import { LoadingDots } from "./styles";

import RequestLimitError from "./classes/RequestLimitError";
import { AppIcon, ErrorIcon } from "./icons";
import { Content } from "./ChatDrawerHome";

import FeaturesCarousel from "./components/FeaturesCarousel";
import StyledButton from "./buttons/StyledButton";

const FIELDS_TYPES = [
  "text (1)",
  "dropdown (3)",
  "date (4)",
  "number (5)",
  "subsheet (sheet attached to a parent sheet) (6)",
  "multiline (8)",
  "file (9)",
  "lookup (referenced to other sheet) (10)",
  "multiselect (11)",
  "boolean (12)",
  "checklist (13)",
  "email (14)",
  "datetime (16)",
  "currency (17)",
  "barcode (18)",
  "formula (19)",
  "phone (20)",
  "percentage (22)",
  "url (24)",
];

const IMAGE_BASE_URL =
  "https://tigersheet.s3.ap-southeast-1.amazonaws.com/tigersheet-assistant";

const IMAGES = [
  {
    title:
      "Utilize specific criteria to apply filters to your records efficiently.",
    imagePath: "filter.png",
  },
  {
    title:
      "Choose from a diverse range of field types tailored to suit your specific requirements.",
    imagePath: "field-types.png",
  },
  {
    title:
      "Customize field configurations to align precisely with your unique requirements.",
    imagePath: "edit-field.png",
  },
  {
    title:
      "You can color rows or cells by selecting them and choosing a fill color in the color menu.",
    imagePath: "color-coding.png",
  },
  {
    title:
      "Inform users via email triggered by predefined actions or events in the system.",
    imagePath: "notifications.png",
  },
  {
    title:
      "Create reminders within the system to notify users of upcoming events or tasks.",
    imagePath: "reminders.png",
  },
  {
    title:
      "Share sheets and packs with controlled user access for secure collaboration.",
    imagePath: "share-by-link.png",
  },
  {
    title:
      "Inform users regarding important updates or upcoming events via bulk email.",
    imagePath: "bulk-email.png",
  },
  {
    title:
      "Generate a range of reports with varied formats and content to address specific requirements or preferences.",
    imagePath: "reports.png",
  },
  {
    title:
      "Workflows structure tasks methodically, boosting efficiency and ensuring streamlined process execution.",
    imagePath: "workflow.png",
  },
  {
    title:
      "Utilize triggers effectively to automate tasks and enhance operational efficiency.",
    imagePath: "triggers.png",
  },
];

const INITIAL_MESSAGES = [
  {
    type: "internal",
    role: "system",
    content: `Tigersheet is a smart spreadsheet management application. it has sheets and groups of sheets (called as a pack). Sheets further has fields of different type: ${FIELDS_TYPES}. Internally these sheets are nothing but database tables with dynamic columns in it. Also we use lookups and subsheets almost everywhere to better define entities and relationships. A sheet's subsheet fields can have nested fields of any type as well as lookup relationship.

Act as the Tigersheet AI assistant. User will give their business requirements which you have to analyze. then ask user to explain every part of requirement to build strong understanding.

You must suggest your idea which should be future-proof, expandable and easy to manage. Ask user's to confirm your suggestion or any updates needed for necessary processing by you.

Important steps for formula fields:
1. For formula field(19) use sql queries format as formulas and current sheet column names as variables for ex. formula for full name will be like CONCAT(%firstname%,%lastname%) where firstname and lastname are columns of current sheet or the sheet which is getting created. for mathematical kind of formula do not use mysql queries format for ex. formula for sum of two field will be \"%field_1% + %field_2%\"
2. All the fields which we are using in the formula as variables has to be present in the sheet or in the sheet which we are creating.
3. While creating a field always check if a field can be a formula field
4. Replace field names present in the formula with filed names present in the current sheet.
5. ALWAYS WRAP THE VARIABLES IN THE FORMULA IN % SIGN.


support@tigersheet.com is the support email.
`,
  },
];

const FUNCTIONS = [
  {
    name: "parseUserRequirements",
    description: "Parse user requirements after he confirms",
    parameters: {
      type: "object",
      properties: {
        packName: {
          type: "string",
          description: "Name of the pack into which sheet will be added",
        },
        packId: {
          type: "number",
          description:
            "ID of the pack if sheet has to be added in existing pack.",
        },
        sheets: {
          type: "array",
          description: "Array of sheets",
          items: {
            type: "object",
            properties: {
              sheetName: {
                type: "string",
                description: "Name of sheet table or schema",
              },
              singularNameOfSheet: {
                type: "string",
                description: "Singular name of the sheet",
              },
              fields: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    field_name: {
                      type: "string",
                      description: "Name of field or column",
                    },
                    attributes: {
                      type: "object",
                      properties: {
                        is_unique: {
                          type: "boolean",
                        },
                        // Don't pass because we need to allow different email ids to be saved in dummy data creation
                        // default_email: {
                        //   type: "boolean",
                        // },
                        format: {
                          type: "string",
                        },
                        options: {
                          type: "array",
                          items: {
                            type: "string",
                          },
                        },
                        source_sheet_name: {
                          type: "string",
                        },
                        source_column_name: {
                          type: "string",
                        },

                        formula: {
                          type: "string",
                        },
                        formula_type: {
                          type: "string",
                          enum: ["date", "text", "number", "currency"],
                        },
                      },
                      description: "attributes for respective field type",
                    },
                    subsheet_fields: {
                      type: "array",
                      description: "List of Fields of a subsheet.",
                      items: {
                        type: "object",
                        properties: {
                          field_name: {
                            type: "string",
                            description:
                              "Name of the field or column for subsheet",
                          },
                          attributes: {
                            type: "object",
                            properties: {
                              is_unique: {
                                type: "boolean",
                              },
                              // Don't pass because we need to allow different email ids to be saved in dummy data creation
                              // default_email: {
                              //   type: "boolean",
                              // },
                              format: {
                                type: "string",
                              },
                              options: {
                                type: "array",
                                items: {
                                  type: "string",
                                },
                              },
                              source_sheet_name: {
                                type: "string",
                              },
                              source_column_name: {
                                type: "string",
                              },
                              formula: {
                                type: "string",
                              },
                              formula_type: {
                                type: "string",
                                enum: ["date", "text", "number", "currency"],
                              },
                            },
                            description:
                              "is_unique, default_email, format, options, only recommended field Attributes for subsheet",
                            enum: [
                              "is_unique",
                              "default_email",
                              "format",
                              "options",
                            ],
                          },
                          field_type_id: {
                            type: "number",
                            description:
                              "Corresponding field type ID for subsheet",
                          },
                        },
                        required: ["field_name", "attributes", "field_type_id"],
                      },
                    },
                    field_type_id: {
                      type: "number",
                      description: "Corresponding field type id",
                    },
                  },
                  required: ["field_name", "attributes", "field_type_id"],
                },
                description:
                  "array of field with required field attributes and field information",
              },
            },
            required: ["sheetName", "singularNameOfSheet", "fields"],
          },
        },
      },
      required: ["sheets", "packName"],
    },
  },
];

const useStyles = makeStyles((theme) => ({
  button: {
    // borderRadius: 18,
    // padding: "0.75rem 1.5rem",
  },
  backDrop: {
    backdropFilter: "blur(2px)",
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  baseDialogPaper: {
    borderRadius: 5,
    minHeight: "30vh",
  },
  dialogPaperInitial: {
    maxHeight: "60vh",
  },
  dialogPaperWithProgress: {
    maxHeight: "80vh",
  },
  linearGradient: {
    background: `linear-gradient(90.22deg,#d3e3fd,#d0f8ff);`,
  },
  dialogContent: {},
  closeButtonContainer: {
    position: "absolute",
    right: 6,
    top: 6,
  },
  headerContainer: {
    padding: "1rem",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    backgroundColor: theme.palette.primary.main,
  },
  header: {
    margin: "0 0.5rem",
    fontSize: 14,
    color: "white",
  },
  btnLabelBase: {
    textTransform: "initial",
    fontWeight: "normal",
  },
  continueActionLabel: {
    fontSize: "16px",
  },
  messagesInputContainer: {
    position: "relative",
    width: "100%",
  },
  rightActionContainer: {
    position: "absolute",
    right: "0.75rem",
    bottom: "0.05rem",
    padding: "0.25rem",
  },
  chatErrorMessage: {
    color: "red",
  },
  chatSuccessMessage: {
    color: "green",
  },
  inProcessDialogContent: {
    height: "100%",

    "&::-webkit-scrollbar": {
      width: "10px",
    },

    "&::-webkit-scrollbar-track": {
      display: "none",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "transparent",
      borderRadius: "4px",
    },

    "&:hover": {
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "lightgray",
      },
    },
  },
  assistantLogoContainer: {
    width: "26px",
    height: "26px",
    borderRadius: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: (props) => props.themeColor || theme.palette.primary.main,
  },
  listContainer: {
    listStyleType: "none",
    padding: 0,
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "12px",
    padding: "0.25rem 0",
    color: "grey",
  },
  listItemError: {
    color: "red",
  },
  listItemCompleted: {
    color: "green",
  },
  listItemLabel: {
    color: "#434343",
    margin: 0,
  },
  onBoardHinCollapseWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  finishedDialogContent: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  errorDialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    width: "100%",
    height: "100%",
    paddingTop: "4rem !important",
    paddingBottom: "3rem !important",
  },

  chatsContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflowY: "auto",
  },
  chatsInnerContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflowY: "auto",

    "&::-webkit-scrollbar": {
      width: "10px",
    },

    "&::-webkit-scrollbar-track": {
      display: "none",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "transparent",
      borderRadius: "4px",
    },

    "&:hover": {
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "lightgray",
      },
    },
  },
  icon: {
    cursor: "auto !important",
  },
}));

function MagicEditPencilIcon(props) {
  return (
    <SvgIcon {...props}>
      <path
        xmlns="http://www.w3.org/2000/svg"
        d="M6.5 12c0-1.5333-.53333-2.83333-1.6-3.9C3.83333 7.03333 2.53333 6.5 1 6.5c1.53333 0 2.83333-.53333 3.9-1.6C5.96667 3.83333 6.5 2.53333 6.5 1c0 1.53333.53333 2.83333 1.6 3.9 1.06667 1.06667 2.3667 1.6 3.9 1.6-1.5333 0-2.83333.53333-3.9 1.6-1.06667 1.06667-1.6 2.3667-1.6 3.9ZM6 19h1.4l9.8-9.775-.725-.725-.7-.7L6 17.6V19Zm-2 2v-4.25L17.2 3.575c.3833-.38333.8583-.575 1.425-.575.5667 0 1.0417.2 1.425.6L21.425 5c.3833.38333.575.85417.575 1.4125 0 .55833-.1917 1.02917-.575 1.4125L8.25 21H4ZM17.2 9.225l-.725-.725-.7-.7L17.2 9.225Z"
        fill="white"
      />
    </SvgIcon>
  );
}

function ChatDialog(props) {
  const {
    open,
    chatStore,
    assistantStore,
    chatSettingsStore,
    chatHelpersStore,
    chatTaskStore,
    userStore,
    onClose,
  } = props;

  const classes = useStyles();

  const inputRef = useRef(null);

  const [tokensState, setTokensState] = useState(null);

  const [text, setText] = useState("");

  const [progress, setProgress] = React.useState(0);
  const [functionFetching, setFunctionFetching] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [viewDetailsButtonEnabled, setViewDetailsButtonEnabled] = useState(true)
  const [errorMessage, setErrorMessage] = useState("");

  const message = useMemo(() => {
    return text.trim();
  }, [text]);

  const { settings, apiKey } = useChatSettings({
    initialSettings: assistantConfig.settings,
    initialApiEndpoints: assistantConfig.apiEndpoints,
  });

  const chatModelHelper = useChatModel({
    models: OPENAI_MODELS,
    modelVariants: OPENAI_MODEL_VARIANTS,
    baseChatModel: settings.baseChatModel,
    useMatchingTokenModel: settings.useMatchingTokenModel,
  });

  const chatCompletionOptions = useMemo(() => {
    const { useApiKey, useProxyServer, apiEndpoints, saveChats, chatConfig } =
      assistantConfig;

    let url = useProxyServer
      ? apiEndpoints.proxyChatCompletion
      : apiEndpoints.chatCompletion;

    if (settings.useInputProxy) {
      url = getValueFromLocalStorage("inputProxy");
    }

    const headers = { ...chatConfig.headers };

    if (useApiKey && apiKey) {
      headers["Authorization"] = `Bearer ${apiKey}`;
    }

    const { include, ...restChatBody } = chatConfig.body;

    const body = {
      ...restChatBody,
      functions: FUNCTIONS,
    };

    return {
      body,
      headers,
      url,
      saveChats,
    };
  }, [apiKey, settings.useInputProxy]);

  const userDisplayName = userStore?.user?.first_name;

  const appTitle = useMemo(() => <>{getTitle()} AI Assistant</>, []);

  const assistantLogo = useMemo(() => {
    const src = getLogoSrc();
    return (
      <img
        src={src}
        alt={appTitle}
        style={{
          height: 18,
        }}
      />
    );
  }, [appTitle]);

  const preLoadImages = () => {
    for (const image of IMAGES) {
      const img = new Image();
      img.src = `${IMAGE_BASE_URL}/${image.imagePath}`;
    }
  };

  const handleClose = () => {
    chatStore.resetChatMessages();
    chatStore.setBackendSessionId(null);
    chatStore.setChatSessionId(null);
    chatStore.resetChatResponse();
    chatStore.resetCurrentAction();
    chatStore.setSubmitCount(0);
    setProgress(0);
    setFunctionFetching(false);
    setErrorMessage("");
    setInProgress(false);
    setText("");
    setTokensState(null);

    typeof onClose === "function" && onClose();
  };

  const handleChatError = (e) => {

    let message = e.message || "Something went wrong";

    const meta = {
      error: true,
      statusCode: e.statusCode,
    };

    const chatParentId = chatStore.getParentNodeId();

    const notificationPayload = {
      chatParentId,
      chatSessionId: chatStore.backendSessionId,
      openAIModel: e.model,
    };

    if (e instanceof RequestLimitError) {
      message = `We are currently facing heavy traffic, due to which we couldn't process this request. Please try again in sometime.`;
    }

    if (e.statusCode) {
      let errorCode = `${chatParentId}-${e.statusCode}`;

      if (e.headers) {
        const orgId = e.headers[CHAT_CONVERSATON_ORG_USED_HEADER];
        if (orgId) {
          errorCode += `-${orgId}`;
          notificationPayload.orgIdUsed = orgId;
        }

        meta[CHAT_CONVERSATON_ORG_USED_HEADER] = orgId;
      }

      message += `\n\nError code: ${errorCode}`;

      notificationPayload.errorCode = errorCode;
      notificationPayload.httpStatus = e.statusCode;
    }

    // send email notification
    if (assistantConfig.settings.enableErrorNotification) {
      chatHelpersStore
        .sendAdminNotification(notificationPayload)
        .then(() => {});
    }


    chatStore.addChatMessage({
      role: "assistant",
      type: "message",
      content: message,
      meta,
    });
    chatStore.onError(e);
  };

  const addLog = (params) => {
    ApiClient.Assistant.addLog(params);
  };

  /**
   * function_call
   */
  const parseUserRequirements = async (params) => {
    try {
      setInProgress(true);
      setFunctionFetching(false); // fetching function_call params is done;

      chatSettingsStore.updateAPiEndpoints({
        chatCompletionUrl: chatCompletionOptions.url,
      });

      const fns = {};

      fitResponseToFunctions(params, fns);
      const tasks = [];

      if (params.packName) {
        tasks.push({
          type: TASK_NAMES.createPacks,
        });
        chatTaskStore.setPacks([{ packName: params.packName }]);
      }

      if (fns[TASK_NAMES.createSheets] && fns[TASK_NAMES.createSheets].length) {
        tasks.push({
          type: TASK_NAMES.createSheets,
        });
        chatTaskStore.setSheets(fns[TASK_NAMES.createSheets]);
      }

      if (fns[TASK_NAMES.createSubsheets]) {
        tasks.push({
          type: TASK_NAMES.createSubsheets,
        });
        chatTaskStore.setSubsheets(fns[TASK_NAMES.createSubsheets]);
      }

      if (
        fns[TASK_NAMES.addLookupFields] &&
        fns[TASK_NAMES.addLookupFields].length
      ) {
        tasks.push({
          type: TASK_NAMES.addLookupFields,
        });
        chatTaskStore.setFields(
          fns[TASK_NAMES.addLookupFields],
          FIELD_TYPES.lookup
        );
      }

      if (
        fns[TASK_NAMES.addFormulaFields] &&
        fns[TASK_NAMES.addFormulaFields].length
      ) {
        tasks.push({
          type: TASK_NAMES.addFormulaFields,
        });
        chatTaskStore.setFields(
          fns[TASK_NAMES.addFormulaFields],
          FIELD_TYPES.formula
        );
      }

      if (fns[TASK_NAMES.createSheets] && fns[TASK_NAMES.createSheets].length) {
        tasks.push({
          type: TASK_NAMES.createDummyData,
        });
      }

      if (fns[TASK_NAMES.createSheets] && fns[TASK_NAMES.createSheets].length) {
        tasks.push({
          type: TASK_NAMES.sheetsNavigation,
        });
      }

      chatTaskStore.setTasks(tasks);
      chatTaskStore.updateTaskIndex(0);
    } catch (e) {
      chatStore.addChatMessage({
        role: "assistant",
        type: "message",
        content: e.message,
      });
    }
  };

  const handleChatFinish = async () => {
    let nodeId = chatStore.backendSessionId ? generateUUID(6) : 0;
    let parentNodeId = chatStore.backendSessionId
      ? chatStore.getParentNodeId()
      : 0;
    const message = {
      nodeId,
      node_id: nodeId,
      parentNodeId,
      parent_row_id: parentNodeId,
      type: "response",
      role: "assistant",
      content: chatStore.response.content,
      functionCall: chatStore.response.functionCall,
    };

    await addLog({ ...message, id: chatStore.backendSessionId });

    chatStore.addChatMessage(message);
    chatStore.onFinish();
    chatStore.resetChatResponse();
    inputRef.current?.focus();
  };

  const handleFunctionCall = async () => {
    chatSettingsStore.updateAPiEndpoints({
      chatCompletionUrl: chatCompletionOptions.url,
    });

    if (!chatStore.response.functionCall) {
      chatStore.onFinish();
      setFunctionFetching(false);
      return;
    }

    const { arguments: args, name } = chatStore.response.functionCall;

    const argumentsObj = JSON.parse(args);
    let nodeId = generateUUID(6),
      parentNodeId = chatStore.getParentNodeId();
    const message = {
      nodeId,
      node_id: nodeId,
      parentNodeId,
      parent_row_id: parentNodeId,
      content: JSON.stringify({ name, arguments: argumentsObj }, null, 4),
      name,
      type: "message",
      role: "function",
    };

    addLog({ id: chatStore.backendSessionId, ...message });
    chatStore.addChatMessage(message);
    chatStore.resetChatResponse();
    chatStore.onFinish();

    const functions = {
      parseUserRequirements,
    };

    chatSettingsStore.updateAPiEndpoints({
      chatCompletionUrl: chatCompletionOptions.url,
    });

    if (typeof functions[name] === "function") {
      await functions[name](argumentsObj);
    }
  };

  const updateProgressOnFunctionCallStep = (params) => {
    setProgress(Number(params.strlen / 10000).toFixed(3));
  };

  const handleChatResponse = (res) => {
    if (res.function_call) {
      setFunctionFetching(true);
    }

    chatStore.onResponse(res, updateProgressOnFunctionCallStep);
  };

  const { fetchResponse, cancelRequest } = useChatCompletion({
    ...chatCompletionOptions,
    onIdle: chatStore.onIdle,
    onStart: chatStore.onStart,
    onResponse: handleChatResponse,
    onFinish: handleChatFinish,
    onError: handleChatError,
    onFunctionCall: handleFunctionCall,
  });

  const isChatLoading = useMemo(() => chatStore.loading, [chatStore.loading]);

  const isLoading = useMemo(() => isChatLoading, [isChatLoading]);

  const messagesToShow = useMemo(() => {
    return [...chatStore.messages].filter((m, i) => {
      m.originalIndex = i;
      return m.type === "message" || m.type === "response";
    });
  }, [chatStore.messages]);

  const messageStream =
    chatStore.response.functionCall.arguments || chatStore.response.content;

  const getLocalItemsToCount = () => {
    let itemsToCount = [...INITIAL_MESSAGES];

    if (chatCompletionOptions.body.functions) {
      itemsToCount.push(chatCompletionOptions.body.functions);
    }

    return itemsToCount;
  };

  const getTokensCount = (objectOrArray) => {
    const messagesStr = JSON.stringify(objectOrArray);
    const encodedMessages = gpt3Encoder.encode(messagesStr);

    return encodedMessages.length;
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const _formSubmitHandler = async ({ message }) => {
    try {
      chatStore.setLoading(true);
      chatStore.setUpdateMessageIndex(undefined);
      let nodeId = generateUUID(6);
      let parentId = chatStore.getParentNodeId();
      let meta_data = {};

      const queryMessage = {
        node_id: nodeId,
        parent_row_id: parentId,
        role: "user",
        content: message,
        type: "message",
        tag:
          chatStore.submitCount === 0
            ? CHAT_MESSAGE_TAGS.firstChatQuery
            : undefined,
      };

      chatStore.addChatMessage(queryMessage, false, false);

      const newMessages = [];

      const { token } = await assistantStore.getChatsAccessToken();
      await chatHelpersStore.getChatConfig();

      if (chatStore.backendSessionId == null) {
        let params = {
          content: message,
          meta_data: JSON.stringify(meta_data),
        };
        ApiClient.Assistant.addLog(params)
          .then((response) => {
            if (response.success) {
              chatStore.setBackendSessionId(response.data?.session_id);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        let params = {
          id: chatStore.backendSessionId,
          node_id: nodeId,
          parent_row_id: parentId,
          nodeId,
          parentNodeId: parentId,
          role: "user",
          content: message,
          type: "message",
        };
        addLog(params);
      }

      newMessages.push(queryMessage);
      chatStore.removeMessage(chatStore.messages.length - 1); // remove the temporarily added query message
      chatStore.addChatMessages(newMessages);

      const itemsToCount = [...getLocalItemsToCount(), ...chatStore.messages];
      const tokensSize = getTokensCount(itemsToCount);
      const model = chatModelHelper.getModel({ tokensSize });

      chatStore.increaseSubmitCount();

      if (!model) {
        setTokensState("EXCEEDED");
        return;
      }

      if (tokensSize >= model.maxTokens * 0.85) {
        setTokensState("WARNING");
      }

      setTokensState(null);

      await fetchResponse(
        {
          messages: [...INITIAL_MESSAGES, ...chatStore.messages],
        },
        {
          model: model.name,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.log(`error->`, error);
    } finally {
      chatStore.setLoading(false);
    }
  };

  const mainChatInputSubmit = async () => {
    if (!message) {
      inputRef.current?.focus();
      return;
    }
    await _formSubmitHandler({
      message,
    });
    setText("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await mainChatInputSubmit();
  };

  const handleAddLog = async (content) => {
    await chatStore.addChatMessage({
      role: "assistant",
      type: "message",
      content: content,
    });
  };

  const onDummyDataStepComplete = (params) => {
    const { log, sheetsCount } = params;
    handleAddLog(log);
    let remainingProgress = Number(1 - progress).toFixed(3);
    let step = Number(remainingProgress / sheetsCount).toFixed(3);
    updateProgressBar(step);
  };

  const updateProgressBar = (progress) => {
    setProgress((prev) => Math.min(prev + +progress, 1));
  };

  const handleSheetsNavigatorSheetClick = async ({ sheet, pack }) => {
    setViewDetailsButtonEnabled(false);
    try {
      const payload = {
        sheetId: chatTaskStore.sheets[0]?.id,
        packId: chatTaskStore.packs[0]?.id,
      };

      await chatHelpersStore.updateSheetPackAfterUpdate(payload);

      userStore.updateUserProperties({
        hasCreatedSheets: true,
        hasCreatedPacks: true,
      });

      chatHelpersStore.navigateToSheet(payload);
      chatTaskStore.reset();
      handleClose();
    } catch (e) {
      console.log(`error navigation->`, e);
    } finally {
      setViewDetailsButtonEnabled(true);
    }
  };

  const handleSheetsNavigatorCancel = () => {
    chatTaskStore.reset();
    handleClose();
  };

  const renderChatStreamResponse = () => {
    if (!isChatLoading && !messageStream) return null;

    return (
      <AssistantChat
        assistantLogo={assistantLogo}
        loading
        content={messageStream}
      />
    );
  };

  const renderChatMessages = () => {
    return (
      <ChatsContainer>
        {messagesToShow.map((chat, index) => {
          if (chat.role === "user")
            return (
              <UserChat displayName={userDisplayName} key={index} {...chat} />
            );
          return (
            <AssistantChat
              key={index}
              assistantLogo={assistantLogo}
              {...chat}
            />
          );
        })}

        {renderChatStreamResponse()}
      </ChatsContainer>
    );
  };

  const renderCloseButton = () => {
    return (
      <div className={classes.closeButtonContainer}>
        <IconButton onClick={handleClose} >
          <CloseOutlinedIcon style={{color:"white"}} />
        </IconButton>
      </div>
    );
  };

  const renderTaskAction = () => {
    if (chatTaskStore.currentTaskIndex === -1) return null;
    const currentTask = chatTaskStore.tasks[chatTaskStore.currentTaskIndex];

    if (!currentTask) return null;

    switch (currentTask.type) {
      case TASK_NAMES.createPacks: {
        return (
          <PacksTaskAction
            packs={chatTaskStore.packs}
            onComplete={(...params) => {
              setProgress((prevProgress) =>
                Number(Math.max(+prevProgress + 0.1, 0.25))
              );
              chatHelpersStore.handlePacksTaskComplete(...params);
            }}
            component="ChatDialog"
          />
        );
      }

      case TASK_NAMES.createSheets: {
        return (
          <SheetsTaskAction
            url={chatCompletionOptions.url}
            body={chatCompletionOptions.body}
            headers={chatCompletionOptions.headers}
            baseChatModel={settings.baseChatModel}
            models={OPENAI_MODELS}
            modelVariants={OPENAI_MODEL_VARIANTS}
            useMatchingTokenModel={settings.useMatchingTokenModel}
            //
            // params={chatTaskStore.queue[0].item.params}
            sheets={chatTaskStore.sheets}
            packId={chatTaskStore.packs?.[0]?.id}
            //
            onComplete={(...params) => {
              setProgress((prevProgress) =>
                Number(Math.max(+prevProgress + 0.1, 0.3))
              );
              chatHelpersStore.handleSheetsTaskComplete(...params);
            }}
            onFetchChatsAccessToken={assistantStore.getChatsAccessToken}
            component="ChatDialog"
          />
        );
      }

      case TASK_NAMES.createSubsheets: {
        return (
          <SubsheetsTaskAction
            onRequestParams={chatHelpersStore.handleGetSubsheetsTaskParams}
            onComplete={(...params) => {
              setProgress((prevProgress) =>
                Number(Math.max(+prevProgress + 0.1, 0.4))
              );
              chatHelpersStore.handleSubsheetsTaskComplete(...params);
            }}
            component="ChatDialog"
          />
        );
      }

      case TASK_NAMES.addGeneralFields: {
        return (
          <AddGeneralFieldsTaskAction
            onRequestParams={
              chatHelpersStore.handleGetAddGeneralFieldsTaskParams
            }
            onComplete={(...params) => {
              setProgress((prevProgress) =>
                Number(Math.max(+prevProgress + 0.1, 0.5))
              );
              chatHelpersStore.handleAddGeneralFieldsTaskComplete(...params);
            }}
            component="ChatDialog"
          />
        );
      }

      case TASK_NAMES.addLookupFields: {
        return (
          <LookupsTaskAction
            onRequestParams={chatHelpersStore.handleGetLookupsTaskParams}
            onComplete={(...params) => {
              setProgress((prevProgress) =>
                Number(Math.max(+prevProgress + 0.1, 0.6))
              );
              chatHelpersStore.handleLookupsTaskComplete(...params);
            }}
            component="ChatDialog"
          />
        );
      }

      case TASK_NAMES.addFormulaFields: {
        return (
          <FormulaeTaskAction
            onRequestParams={chatHelpersStore.handleGetFormulaeTaskParams}
            onComplete={(...params) => {
              setProgress((prevProgress) =>
                Number(Math.max(+prevProgress + 0.1, 0.7))
              );
              chatHelpersStore.handleFormulaeTaskComplete(...params);
            }}
            component="ChatDialog"
          />
        );
      }

      case TASK_NAMES.createDummyData: {
        return (
          <CreateDummyDataTaskAction
            onRequestParams={chatHelpersStore.handleGetDummyDataTaskParams}
            onStepComplete={onDummyDataStepComplete}
            onComplete={(...params) => {
              // setProgress((prevProgress) => Math.min(prevProgress + 0.1, 1));
              chatHelpersStore.handleDummyDataTaskComplete(...params);
            }}
            component="ChatDialog"
          />
        );
      }

      // case TASK_NAMES.sheetsNavigation: {
      //   return (
      //     <NavigationTaskAction
      //       onRequestParams={chatHelpersStore.handleGetNavigatorTaskParams}
      //       onSheetClick={handleSheetsNavigatorSheetClick}
      //       onCancel={handleSheetsNavigatorCancel}
      //     />
      //   );
      // }

      default: {
        return null;
      }
    }
  };

  const renderInProgressContents = () => {
    return (
      <>
        <DialogTitle className={classes.headerContainer}>
          <MagicEditPencilIcon className={classes.icon} />
          <span className={classes.header}>
            The AI is spinning its magic to produce your tigersheets
          </span>
        </DialogTitle>
        <DialogContent className={classes.inProcessDialogContent}>
          {/* feature carousel */}
          <FeaturesCarousel
            enabled={inProgress || functionFetching}
            images={IMAGES}
            imageBaseUrl={IMAGE_BASE_URL}
          />
          {/* progress-bar */}
          <div style={{ padding: "1rem 0" }}>
            <StyledLinearProgress variant="determinate" value={progress * 100} />
          </div>
          {renderTaskAction()}
        </DialogContent>
      </>
    );
  };

  const renderErrorContent = () => {
    return (
      <StyledDialogContent className={classes.errorDialogContent}>
        <ErrorIcon />
        <h1 style={{ marginTop: "5rem", color: "#5A5A5A" }}>
          Something went wrong!
        </h1>
        <p style={{ marginBottom: "0.5rem", color: "#5A5A5A" }}>
          {errorMessage || "Seems like something failed while processing."}
        </p>
        <div style={{ paddingTop: "1rem" }}>
          <Button
            color="primary"
            classes={{ label: classes.btnLabelBase }}
            onClick={onClose}
          >
            Close this
          </Button>
        </div>
      </StyledDialogContent>
    );
  };

  const renderOnBoardingFinishContent = () => {
    return (
      <>
        <DialogTitle className={classes.headerContainer}>
          <MagicEditPencilIcon className={classes.icon} />
          <span className={classes.header}>Tigersheet Explorer</span>
        </DialogTitle>
        <StyledDialogContent className={classes.finishedDialogContent}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              paddingBottom: "1rem",
              paddingTop:"1rem"
            }}
          >
            <AppIcon size={60} />
            <h1 style={{ color: "#5A5A5A",paddingTop:"6px" }}>Congratulations!</h1>
          </div>
          <p style={{ fontSize: 14 }}>
            We have created the necessary pack and sheets for your requirements
            <br />
            with some sample data added into it.
          </p>
          <p style={{ fontSize: 14 }}>
            Please click on "<strong>View details</strong>" to view the created pack, sheets and
            fields.
          </p>
        </StyledDialogContent>
        <DialogActions style={{
          padding:"0rem 1rem 1rem 0rem"
        }} >
          <Button onClick={handleSheetsNavigatorCancel}>Cancel</Button>
          <Button
            disabled={!viewDetailsButtonEnabled}
            color="primary"
            variant="contained"
            disableElevation
            endIcon={<FaArrowRight color="inherit" size={12} />}
            onClick={handleSheetsNavigatorSheetClick}
          >
            View details
          </Button>
        </DialogActions>
      </>
    );
  };

  const renderDialogContent = () => {
    const isSubmitDisabled =
      isLoading ||
      !message ||
      (assistantConfig.useApiKey && !apiKey) ||
      tokensState === "EXCEEDED";

    const inputDisabled =
      isLoading ||
      (assistantConfig.useApiKey && !apiKey) ||
      tokensState === "EXCEEDED";

    if (
      chatTaskStore.tasks[chatTaskStore.currentTaskIndex]?.type ===
      TASK_NAMES.sheetsNavigation
    ) {
      return renderOnBoardingFinishContent();
    }

    if (inProgress || functionFetching) {
      return renderInProgressContents();
    }

    if (chatStore.submitCount === 0) {
      return (
        <>
          <DialogTitle className={classes.headerContainer}>
            <MagicEditPencilIcon className={classes.icon} />
            <span className={classes.header}>
              Hi, help me setup by describing your business and requirements
            </span>
            {renderCloseButton()}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <StyledTextArea
              ref={inputRef}
              disabled={inputDisabled}
              autoFocus
              rowsMin={1}
              rowsMax={10}
              placeholder="e.g. I run an online store for selling household stuffs. I am looking to manage it's sells, stocks, categories, discounts on them, tracking delivery etc."
              value={text}
              onChange={handleTextChange}
            />
          </DialogContent>
          <DialogActions>
            <IconButton
              disabled={isSubmitDisabled}
              variant="contained"
              color="primary"
              disableElevation
              className={classes.button}
              onClick={handleSubmit}
            >
              <MdSend color="inherit" size={20} />
            </IconButton>
          </DialogActions>
        </>
      );
    }

    if (errorMessage) return renderErrorContent();

    return (
      <>
        <DialogTitle className={classes.headerContainer}>
          <MagicEditPencilIcon className={classes.icon} />
          <span className={classes.header}>
            Hi, help me setup by describing your business and requirements
          </span>
          {renderCloseButton()}
        </DialogTitle>
        <ScrollToBottom
          className={classes.chatsContainer}
          scrollViewClassName={classes.chatsInnerContainer}
          followButtonClassName="scrollToBottomButton"
        >
          <Content>{renderChatMessages()}</Content>
          {isLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <LoadingDots className="loading-dots-chat-dialog" />
            </div>
          )}
        </ScrollToBottom>
        <DialogActions>
          <div className={classes.messagesInputContainer}>
            <ChatInputTextArea
              ref={inputRef}
              disabled={inputDisabled}
              autoFocus
              rowsMin={3}
              rowsMax={5}
              placeholder="Write your message here"
              value={text}
              onChange={handleTextChange}
            />
            <div className={classes.rightActionContainer}>
              <StyledIconButton color="primary" disabled={isSubmitDisabled} onClick={handleSubmit}>
                <MdSend color="inherit" size={20} />
              </StyledIconButton>
            </div>
          </div>
        </DialogActions>
      </>
    );
  };

  const renderChatDialog = () => {
    return (
      <Dialog
        open={open}
        TransitionComponent={Zoom}
        classes={{
          paper: cx(
            classes.baseDialogPaper,
            inProgress || functionFetching
              ? classes.dialogPaperWithProgress
              : classes.dialogPaperInitial,
            !text && chatStore.submitCount < 1 && classes.linearGradient
          ),
          root: classes.backDrop,
        }}
        fullWidth
        maxWidth="sm"
      >
        {renderDialogContent()}
      </Dialog>
    );
  };

  useEffect(() => {
    if (open) preLoadImages();
  }, [open]);

  return renderChatDialog();
}

export default inject(({ store }) => {
  return {
    chatStore: store.chatStore,
    chatSettingsStore: store.chatSettingsStore,
    assistantStore: store.assistantStore,
    userStore: store.userStore,
    chatTaskStore: store.chatTaskStore,
    onBoardingStore: store.onBoardingStore,
    chatHelpersStore: store.chatHelpersStore,
  };
})(observer(ChatDialog));

const StyledDialogContent = styled(DialogContent)`
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: lightgray;
    border-radius: 4px;
  }
`;

const StyledTextArea = styled(TextareaAutosize)`
  & {
    width: 100%;
    resize: none;
    background: transparent;
    border: none;
    font-size: 16px;
    &::-webkit-scrollbar {
      width: 8px;
      cursor: pointer;
    }

    &::-webkit-scrollbar-thumb {
      background-color: lightgray;
      border-radius: 4px;
      cursor: pointer;
    }

    &:disabled {
      color: gray;
    }
  }
`;

const ChatInputTextArea = styled(StyledTextArea)`
  & {
    padding: 1rem;
    font-size: 14px;
  }
`;

const ChatsContainer = styled.div`
  & {
    display: flex;
    flex-direction: column;
  }
`;
